import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  TextField,
  FormControlLabel,
  Typography,
  Box,
  Divider,
  IconButton,
  InputAdornment,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import PropTypes from "prop-types";

const OemTargetECUTypePopup = ({
  open,
  onClose,
  ecuTypeObjList = [],
  makes = [],
  ecuTypes = [],
  exclusions = [],
  onSave,
  onRefresh,
  reviewStatus,
}) => {
  const [search, setSearch] = useState("");
  const [selectedMakes, setSelectedMakes] = useState([]);
  const [selectedECUs, setSelectedECUs] = useState([]);
  const isSavingRef = useRef(false);

  useEffect(() => {
    // Initialize when dialog opens and we're not in a save operation
    if (open && !isSavingRef.current) {   
      // Initialize selected ECUs with current exclusions
      setSelectedECUs([...exclusions]);

      // Initialize makes based on exclusions
      const selectedMakesInit = makes.filter((make) => {
        // Make is selected if it has any excluded ECU types
        return exclusions.some((exclusion) => {
          // This is a simplified check - in real scenario you might need to map makes to ECU types
          return exclusions.length > 0;
        });
      });

      setSelectedMakes(selectedMakesInit);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, exclusions]);

  const handleECUChange = (ecuNameWithoutMake, makePrefix) => {
    // Construct the full ECU name with make prefix
    const fullEcuName = `${makePrefix}_${ecuNameWithoutMake}`;
    
    if (selectedECUs.includes(fullEcuName)) {
      // Remove from exclusions
      setSelectedECUs((prev) => prev.filter((e) => e !== fullEcuName));
    } else {
      // Add to exclusions
      setSelectedECUs((prev) => [...prev, fullEcuName]);
    }
  };

  const handleMakeChange = (make) => {
    if (selectedMakes.includes(make)) {
      // Deselect make - remove all ECUs for this make from exclusions
      setSelectedMakes((prev) => prev.filter((m) => m !== make));
      // In a real scenario, you'd filter ECUs by make
      setSelectedECUs([]);
    } else {
      // Select make - add all ECUs for this make to exclusions
      setSelectedMakes((prev) => [...prev, make]);
      // In a real scenario, you'd add ECUs for this make
      setSelectedECUs([...ecuTypes]);
    }
  };

  const handleClearAll = () => {
    const allECUFullNames = [];
    ecuTypeObjList.forEach(item => {
      item.ecuTypes.forEach(ecuType => {
        allECUFullNames.push(`${item.make}_${ecuType}`);
      });
    });   
    exclusions.forEach(exclusion => {
      if (!allECUFullNames.includes(exclusion)) {
        allECUFullNames.push(exclusion);
      }
    });
    
    setSelectedMakes([]);
    setSelectedECUs(allECUFullNames);
  };

  const handleSave = async () => {
    isSavingRef.current = true;

    try {
      if (onSave && typeof onSave === "function") {
        onClose();
        await onSave("ecuType_exclusion", selectedECUs);
        
        // Call parent refresh function with updated count if provided
        if (onRefresh && typeof onRefresh === "function") {
          onRefresh(selectedECUs.length);
        }
        
      }
    } catch {
      // The selectedECUs state remains unchanged on error
    } finally {
      isSavingRef.current = false;
    }
  };

  const handleCancel = () => {
    // Reset to original values
    setSelectedECUs([...exclusions]);
    onClose();
  };

  const isRestricted =
    reviewStatus.includes("FORCE_UNINSTALL") || reviewStatus.includes("DELIST");

  // Get all unique ECU type names (without make prefix) from both ecuTypes and exclusions
  const getECUNameFromExclusion = (exclusion) => {
    const parts = exclusion.split('_');
    return parts.slice(1).join('_');
  };

  // Combine ecuTypes and stripped exclusion names
  const allECUNames = [...new Set([
    ...ecuTypes,
    ...exclusions.map(getECUNameFromExclusion)
  ])];

  // Create a map to find which make each ECU belongs to
  const ecuToMakeMap = {};
  ecuTypeObjList.forEach(item => {
    item.ecuTypes.forEach(ecuType => {
      ecuToMakeMap[ecuType] = item.make;
    });
  });

  // Also map exclusions to their makes
  exclusions.forEach(exclusion => {
    const parts = exclusion.split('_');
    const make = parts[0];
    const ecuName = parts.slice(1).join('_');
    if (!ecuToMakeMap[ecuName]) {
      ecuToMakeMap[ecuName] = make;
    }
  });

  // Filter ECU types for display
  const filteredECUTypes = allECUNames.filter((ecu) =>
    ecu.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            width: "585px",
            height: "412px",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 600,
          padding: "5px 24px",
        }}
      >
        Targeting - ECU Type
        <IconButton onClick={handleCancel}>
          <ClearIcon sx={{ color: "#2A2C2D" }} />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          backgroundColor: "#fff",
          padding: "0px 24px 0px 24px",
          display: "flex",
          flexDirection: "column",
          gap: "15px",
        }}
      >
        <TextField
          fullWidth
          placeholder="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          margin="dense"
          sx={{ marginTop: "0px", marginBottom: "0px" }}
          variant="outlined"
          size="small"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
        />

        <Box
          display="flex"
          sx={{
            border: "1px solid transparent",
            gap: "18px",
            backgroundColor: " #F3F3F3",
            borderRadius: "8px",
            padding: "10px 12px 16px 12px",
            height: "220px",
          }}
        >
          <Box flex={1}>
            <Typography variant="subtitle2" gutterBottom>
              <strong>Make</strong>
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <Box
              sx={{
                maxHeight: "167px",
                overflowY: "auto",
                pr: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {makes.map((make) => (
                <FormControlLabel
                  sx={{ marginLeft: "0px !important" }}
                  key={make}
                  control={
                    <Checkbox
                      checked={true}
                      onChange={() => handleMakeChange(make)}
                      size="small"
                      disabled={true}
                    />
                  }
                  label={<Typography variant="body2">{make}</Typography>}
                />
              ))}
            </Box>
          </Box>

          <Box flex={1}>
            <Typography variant="subtitle2" gutterBottom>
              <strong>ECU Types</strong>
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <Box
              sx={{
                maxHeight: "190px",
                overflowY: "auto",
                pr: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {filteredECUTypes.map((ecu) => {
                const makePrefix = ecuToMakeMap[ecu];
                const fullEcuName = makePrefix ? `${makePrefix}_${ecu}` : ecu;
                const isExcluded = selectedECUs.includes(fullEcuName);

                return (
                  <FormControlLabel
                    key={ecu}
                    control={
                      <Checkbox
                        checked={!isExcluded}
                        onChange={() => handleECUChange(ecu, makePrefix)}
                        disabled={isRestricted}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">{ecu}</Typography>}
                  />
                );
              })}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{ px: 3, py: 2, paddingTop: "15px", paddingBottom: "12px" }}
      >
        <Button
          onClick={isRestricted ? undefined : handleClearAll}
          sx={{
            textTransform: "none",
            fontWeight: 400,
            mr: "auto",
            color: "#5A5B5C",
            opacity: isRestricted ? 0.5 : 1,
            pointerEvents: isRestricted ? "none" : "auto",
          }}
        >
          Clear All
        </Button>

        <Button
          onClick={handleCancel}
          sx={{
            textTransform: "none",
            fontWeight: 400,
            width: "114px",
            height: "36px",
            borderRadius: "4px",
            backgroundColor: "#1214151A",
            color: "#2A2C2D",
          }}
        >
          Cancel
        </Button>
        {!isRestricted && (
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{
              textTransform: "none",
              fontWeight: 400,
              width: "114px",
              height: "36px",
              borderRadius: "4px",
              backgroundColor: "#0476E2",
              color: "#FFFFFF",
            }}
          >
            SAVE
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OemTargetECUTypePopup;

OemTargetECUTypePopup.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  ecuTypeObjList: PropTypes.array,
  makes: PropTypes.array,
  ecuTypes: PropTypes.array,
  exclusions: PropTypes.array,
  onSave: PropTypes.func,
  onRefresh: PropTypes.func,
  reviewStatus: PropTypes.string,
};
